import React, { useState } from "react";
import logo from "../../assets/images/80eightLogo.png";
import { Input } from "../../components/ui/input-field";
import google from "../../assets/icons/google.svg";
import apple from "../../assets/icons/apple.svg";
import { api } from "../../services/axiosService";
import { setAuthData } from "../../context/AuthActions";
import { setAuthorization } from "../../services/apiService";
import Toaster from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";

export default function Register() {
  const { toast } = useToast();
  const { refreshUser, handleLogout } = useAuth();
  const { loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);

  const passwordRequirements = [
    {
      id: "length",
      label: "At least 8 characters",
      validator: (pwd) => pwd.length >= 8,
    },
    {
      id: "lowercase",
      label: "One lowercase letter (a-z)",
      validator: (pwd) => /(?=.*[a-z])/.test(pwd),
    },
    {
      id: "uppercase",
      label: "One uppercase letter (A-Z)",
      validator: (pwd) => /(?=.*[A-Z])/.test(pwd),
    },
    {
      id: "number",
      label: "One number (0-9)",
      validator: (pwd) => /(?=.*[0-9])/.test(pwd),
    },
    {
      id: "special",
      label: "One special character (!@#$%^&*)",
      validator: (pwd) => /(?=.*[!@#$%^&*])/.test(pwd),
    },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else {
      const failedRequirements = passwordRequirements
        .filter((req) => !req.validator(password))
        .map((req) => req.label);

      if (failedRequirements.length > 0) {
        newErrors.password = `Password requirements not met: ${failedRequirements.join(", ")}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setPasswordTouched(true);
    const allRequirementsMet = passwordRequirements.every((req) =>
      req.validator(e.target.value)
    );
    setShowPasswordRequirements(!allRequirementsMet);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      try {
        const signupResponse = await api.post("/api/v1/user/sign-up", {
          email,
          password,
        });

        if (signupResponse.data?.status) {
          toast({
            description: "Signup successful!",
            variant: "success",
          });

          const signinResponse = await api.post("/api/v1/user/sign-in", {
            email,
            password,
          });

          if (signinResponse.data?.status) {
            const { access_token } = signinResponse.data.data;

            // IMPORTANT: Store token in localStorage FIRST so axiosService can use it
            setAuthData({
              token: access_token,
              userId: null, // Will be updated after profile fetch
              email: email,
              session: null, // Will be updated after profile fetch
              accountId: null, // Will be updated after profile fetch
            });

            // Set authorization header for apiService
            setAuthorization(access_token);

            // Fetch user profile to get all needed data
            try {
              const userResponse = await api.get("/api/v1/user/get-account");

              if (userResponse.data?.data) {
                const userData = userResponse.data.data;

                // Build session object
                const session = {
                  aud: process.env.REACT_APP_AUTH_WEB_CLIENT,
                  email: userData.email,
                  emailVerified: userData.emailVerified,
                  name: `${userData.personal_info?.name || ""} ${
                    userData.personal_info?.lastname || ""
                  }`.trim(),
                  updatedAt: userData.updatedAt,
                  sub: userData.authId || userData.userId,
                };

                // Update all auth data in localStorage with complete info
                setAuthData({
                  token: access_token,
                  userId: userData.authId || userData.userId,
                  email: userData.email,
                  session: session,
                  accountId: userData.accountId,
                });

                // Refresh user profile in context
                await refreshUser();

                toast({
                  description: "Account created successfully!",
                  variant: "success",
                });

                // Navigate to email verification
                navigate("/onboarding/verify-email");
              }
            } catch (error) {
              console.log(error);
              console.error(
                "Failed to fetch user profile after signup:",
                error
              );
              toast({
                description: error || "Failed to sign in after signup",
                variant: "error",
              });
              handleLogout();
            }
          } else {
            toast({
              description:
                signinResponse.data?.message ||
                "Failed to sign in after signup",
              variant: "error",
            });
          }
        } else {
          toast({
            description:
              signupResponse.data?.message || "Failed to create account",
            variant: "error",
          });
        }
      } catch (error) {
        console.error("Signup error:", error);
        toast({
          description: error?.message || error || "Failed to create account",
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleGoogleSignup = () => {
    loginWithRedirect({
      authorizationParams: {
        connection: "google-oauth2",
        redirect_uri: `${window.location.origin}/login`,
        screen_hint: "signup",
        prompt: "select_account",
      },
    });
  };

  const handleAppleSignup = () => {
    loginWithRedirect({
      authorizationParams: {
        connection: "apple",
        redirect_uri: `${window.location.origin}/login`,
        screen_hint: "signup",
      },
    });
  };

  return (
    <div className="min-h-screen text-gray-900 transition-colors dark:text-gray-100">
      <div className="flex flex-col w-full h-screen bg-black">
        <div className="flex flex-col items-center justify-center h-full mx-auto">
          <form
            onSubmit={handleSubmit}
            className="bg-white border-[#F1EDF7] gap-5 lg:w-[477px] md:w-[477px] w-[363px] rounded-[14px] lg:p-12 md:p-12 p-4 flex flex-col justify-center items-center"
          >
            <img src={logo} alt="" className="w-[92px] h-[92px]" />
            <div>
              <h1 className="font-plusjakarta text-text-primary font-semibold text-[20px] leading-[24px] text-center">
                Welcome to 80eight
              </h1>
              <p className="font-plusjakarta text-text-secondary py-2 px-10 text-[14px] text-center">
                Enter your details below to create your account
              </p>
            </div>

            <div className="flex flex-col gap-5 w-full">
              <div className="flex flex-col gap-3">
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  state={errors.email ? "error" : "default"}
                  helperText={errors.email || ""}
                  className="!py-[27px] border-[1px] text-[16px] font-plusjakarta"
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => setPasswordTouched(true)}
                  state={errors.password ? "error" : "default"}
                  helperText={""}
                  className="!py-[27px] border-[1px] text-[16px] font-plusjakarta"
                />

                {passwordTouched && showPasswordRequirements && (
                  <div className="mt-2 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Password must contain:
                    </p>
                    <ul className="space-y-1">
                      {passwordRequirements.map((requirement) => (
                        <li
                          key={requirement.id}
                          className={`text-xs flex items-center ${
                            requirement.validator(password)
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          <span
                            className={`inline-block w-1 h-1 rounded-full mr-2 ${
                              requirement.validator(password)
                                ? "bg-green-600"
                                : "bg-red-600"
                            }`}
                          />
                          {requirement.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="font-plusjakarta px-6 py-3 mt-2 text-base font-semibold rounded-lg text-white bg-[#33125F] hover:bg-[#33115F] disabled:opacity-70 focus:outline-none flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating account...
                  </>
                ) : (
                  "Create account"
                )}
              </button>

              <p className="self-start">
                <span className="font-plusjakarta text-[#423E51] text-sm">
                  Already have an account?
                </span>
                <Link
                  to="/login"
                  className="font-semibold text-sm text-[#33125F] font-plusjakarta ml-3 cursor-pointer"
                >
                  Log in
                </Link>
              </p>

              <div className="flex items-center w-full">
                <div className="flex-grow border-t border-black/10" />
                <span className="mx-4 text-[16px] text-[#6D6584]">OR</span>
                <div className="flex-grow border-t border-black/10" />
              </div>

              <button
                type="button"
                onClick={handleGoogleSignup}
                className="w-full px-5 py-[13px] text-[#6D6584] flex items-center gap-2 rounded-lg border border-gray-300 bg-white text-sm font-plusjakarta font-semibold hover:bg-gray-50"
              >
                <img src={google} alt="Google" className="w-5 h-5" />
                Continue with Google
              </button>

              <button
                type="button"
                onClick={handleAppleSignup}
                className="w-full px-5 py-[13px] text-[#6D6584] flex items-center gap-2 rounded-lg border border-gray-300 bg-white text-sm font-plusjakarta font-semibold hover:bg-gray-50"
              >
                <img src={apple} alt="Apple" className="w-5 h-5" />
                Continue with Apple
              </button>
            </div>
          </form>
        </div>
      </div>
      <Toaster />
    </div>
  );
}
